import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import useVisitorSession from "@/hooks/useVisitorSession";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { initMobile, isMobile } from "@/lib/mobile";
import SplashScreen from "@/components/SplashScreen";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import ServicesPage from "@/pages/ServicesPage";
import BookingPage from "@/pages/BookingPage";
import MyBookingsPage from "@/pages/MyBookingsPage";
import VIPPage from "@/pages/VIPPage";
import VIPOnboardingPage from "@/pages/VIPOnboardingPage";
import ProviderApplyPage from "@/pages/ProviderApplyPage";
import ProviderDashboard from "@/pages/ProviderDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminBankingPage from "@/pages/AdminBankingPage";
import VenueRegisterPage from "@/pages/VenueRegisterPage";
import VenueDashboard from "@/pages/VenueDashboard";
import VehiclesPage, { ListVehiclePage } from "@/pages/VehiclesPage";
import WheelsApplyPage from "@/pages/WheelsApplyPage";
import ShippingPage from "@/pages/ShippingPage";
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "@/pages/AuthRecoveryPages";
import ReferralsPage from "@/pages/ReferralsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelledPage from "@/pages/PaymentCancelledPage";
import BookingDetailPage from "@/pages/BookingDetailPage";
import AuthCallback from "@/pages/AuthCallback";
import MarketplacePage, { MarketplaceDetailPage } from "@/pages/MarketplacePage";
import GiftCardPage from "@/pages/GiftCardPage";
import SendHomePage from "@/pages/SendHomePage";
import JoinPage from "@/pages/JoinPage";
import ProviderQuickStartPage from "@/pages/ProviderQuickStartPage";
import AdvertisePage from "@/pages/AdvertisePage";
import { AboutPage, RefundPolicyPage, PrivacyPolicyPage } from "@/pages/PolicyPages";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import WhatsAppButton from "@/components/WhatsAppButton";
import BottomNavBar from "@/components/BottomNavBar";
import ConciergeChat from "@/components/ConciergeChat";
import "@/App.css";

function ProtectedRoute({ children, role }) {
    const { user } = useAuth();
    if (user === undefined) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="overline text-muted-foreground">Loading...</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/auth?mode=login" replace />;
    if (role && user.role !== role && user.role !== "admin") {
        return <Navigate to="/home" replace />;
    }
    return children;
}

function AppRoutes() {
    const location = useLocation();
    // Emergent Google OAuth returns to <origin>/auth/callback#session_id=...
    // Detect the fragment synchronously so we take the callback route BEFORE
    // any ProtectedRoute check kicks in.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (location.hash && location.hash.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/vip" element={<VIPPage />} />
            <Route path="/vip/onboarding" element={<ProtectedRoute><VIPOnboardingPage /></ProtectedRoute>} />
            <Route path="/become-provider" element={<ProviderApplyPage />} />
            <Route path="/join-as-pro" element={<Navigate to="/become-provider" replace />} />
            <Route path="/venue/register" element={<ProtectedRoute><VenueRegisterPage /></ProtectedRoute>} />
            <Route path="/venue/dashboard" element={<ProtectedRoute><VenueDashboard /></ProtectedRoute>} />
            <Route path="/vehicles" element={<VehiclesPage />} />
            <Route path="/vehicles/list" element={<ProtectedRoute><ListVehiclePage /></ProtectedRoute>} />
            <Route path="/wheels/apply" element={<WheelsApplyPage />} />
            <Route path="/drive" element={<Navigate to="/wheels/apply" replace />} />
            <Route path="/shipping" element={<ShippingPage />} />
            <Route path="/logistics" element={<Navigate to="/shipping" replace />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/marketplace/:id" element={<MarketplaceDetailPage />} />
            <Route path="/giftcards" element={<GiftCardPage />} />
            <Route path="/send-home" element={<SendHomePage />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/advertise" element={<AdvertisePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/refer" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
            <Route path="/book/:serviceId" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
            <Route path="/my-bookings" element={<ProtectedRoute><MyBookingsPage /></ProtectedRoute>} />
            <Route path="/bookings/:bookingId" element={<ProtectedRoute><BookingDetailPage /></ProtectedRoute>} />
            <Route path="/provider" element={<ProtectedRoute role="provider"><ProviderDashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/banking" element={<ProtectedRoute role="admin"><AdminBankingPage /></ProtectedRoute>} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    useVisitorSession();
    const [splashDone, setSplashDone] = useState(() => {
        try {
            return sessionStorage.getItem("smart_splash_done") === "1";
        } catch {
            return false;
        }
    });

    useEffect(() => {
        initMobile();
        // Lock page title to the full legal entity name on every navigation
        document.title = "SMART DEMANDS 24/7 LIMITED · Your Demands, 24/7";
        if (splashDone) {
            try {
                sessionStorage.setItem("smart_splash_done", "1");
            } catch {
                /* sessionStorage unavailable in some embeds */
            }
        }
        // Register PWA service worker (skipped on Capacitor native shell — it has its own runtime)
        if (
            "serviceWorker" in navigator &&
            !window.Capacitor &&
            window.location.protocol === "https:"
        ) {
            window.addEventListener("load", () => {
                navigator.serviceWorker
                    .register("/service-worker.js")
                    .catch((err) => console.warn("SW registration failed", err));
            });
        }
    }, [splashDone]);

    return (
        <div className={`App ${isMobile() ? "mobile-native" : ""}`}>
            {!splashDone && <SplashScreen onDone={() => setSplashDone(true)} />}
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster theme="dark" position="top-right" />
                    <PWAInstallPrompt />
                    <WhatsAppButton />
                    <ConciergeChat />
                    <BottomNavBar />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}
